import { useEffect, useRef, useState } from 'react';
import {
  Wallet,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Edit2,
  Save,
  Plus,
  Minus,
  History,
  Building2,
  CreditCard,
  AlertCircle,
  CheckCircle,
  Clock,
  Phone,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Download,
} from 'lucide-react';

interface LocationBilling {
  location_id: string;
  company_id: string;
  location_name: string | null;
  wallet_balance: number;
  plan_id: string | null;
  plan_name: string | null;
  auto_recharge_enabled: boolean;
  auto_recharge_threshold: number;
  auto_recharge_amount: number;
  negative_balance_threshold: number;
  stripe_customer_id: string | null;
  twilio_sub_account_sid: string | null;
  subscription_status: string | null;
  is_internal: boolean;
  has_billing_config: boolean;
  created_at: string;
  updated_at: string;
}

type SortBy = 'name' | 'wallet' | 'created';
type SortDir = 'asc' | 'desc';

const DEFAULT_SORT: SortBy = 'name';
const DEFAULT_DIR: SortDir = 'asc';
const DEFAULT_LIMIT = 20;
const LIMIT_OPTIONS = [20, 50, 100];

function readInitialListParams() {
  if (typeof window === 'undefined') {
    return { search: '', page: 1, sortBy: DEFAULT_SORT, sortDir: DEFAULT_DIR, limit: DEFAULT_LIMIT };
  }
  const p = new URLSearchParams(window.location.search);
  const sortRaw = p.get('sort');
  const dirRaw = p.get('dir');
  const limitRaw = parseInt(p.get('limit') || '', 10);
  return {
    search: p.get('search') || '',
    page: Math.max(1, parseInt(p.get('page') || '1', 10) || 1),
    sortBy: (sortRaw === 'wallet' || sortRaw === 'created' || sortRaw === 'name' ? sortRaw : DEFAULT_SORT) as SortBy,
    sortDir: (dirRaw === 'desc' ? 'desc' : 'asc') as SortDir,
    limit: LIMIT_OPTIONS.includes(limitRaw) ? limitRaw : DEFAULT_LIMIT,
  };
}

interface LocationDetail extends LocationBilling {
  stripe_subscription_id: string | null;
  twilio_sub_account_auth: string | null;
  messaging_service_sid: string | null;
  billing_email: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  negative_balance_threshold: number;
  billing_suspended_at: string | null;
  admin_suspended_at: string | null;
  plan_suspended_at: string | null;
  payment_methods: PaymentMethod[];
}

interface PaymentMethod {
  id: string;
  stripe_payment_method_id: string;
  type: string;
  last4: string | null;
  brand: string | null;
  exp_month: number | null;
  exp_year: number | null;
  is_primary_subscription: boolean;
  is_primary_wallet: boolean;
  is_active: boolean;
}

interface WalletTransaction {
  id: string;
  transaction_type: string;
  amount: number;
  balance_before: number;
  balance_after: number;
  description: string | null;
  reference_id: string | null;
  created_at: string;
}

interface Plan {
  id: string;
  plan_name: string;
  price: number;
  billing_period: string;
  is_active: boolean;
}

interface PhoneLine {
  id: string;
  phone_number: string;
  twilio_phone_sid: string | null;
  friendly_name: string | null;
  capabilities: {
    voice: boolean;
    sms: boolean;
    mms: boolean;
  } | null;
  monthly_cost: number;
  customer_charge: number;
  status: string;
  provisioned_date: string | null;
  next_bill_date: string | null;
  created_at: string;
  updated_at: string;
}

// Stage 1d: Twilio webhook event recovery types
interface RecoverySummary {
  accountSid: string;
  billingDisplayName: string | null;
  isInternal: boolean;
  unresolvedCount: number;
  oldestSeen: string | null;
  newestSeen: string | null;
  eventTypeBreakdown: Record<string, number>;
  estimatedWalletImpact: number;
  breakdownSampleSize: number;
  breakdownIsEstimate: boolean;
  replayBatchSize: number;
}

interface ReplayProgress {
  totalProcessed: number;
  totalDuplicates: number;
  totalErrored: number;
  totalRowsResolved: number;
  batchesRun: number;
  hasMore: boolean;
  sampleErrors: { rowId: string; error: string }[];
}

// Communications backfill (Twilio Event Streams gap recovery)
interface BackfillPreviewDay {
  date: string;
  messages: number;
  sms: number;
  mms: number;
  voice: number;
  estCharge: number;
  scanned: boolean;
}
interface BackfillPreview {
  billingDisplayName: string | null;
  isInternal: boolean;
  dateStart: string;
  dateEnd: string;
  includeVoice: boolean;
  currentBalance: number;
  estTotalCharge: number;
  projectedEndBalance: number;
  capped: boolean;
  pageCap: number;
  perDay: BackfillPreviewDay[];
}
// Subset of the row shape returned by GET /admin/batches
// (api.get_admin_batches_with_pipeline_status).
interface DownloadBatchRow {
  id: string;
  batch_name: string | null;
  created_at: string;
  total_contacts: number | null;
  clean_count: number | null;
  status: string;
}

const DOWNLOAD_PAGE_SIZE = 100;

interface BackfillRun {
  id: string;
  date_start: string;
  date_end: string;
  include_voice: boolean;
  fire_mode: 'fire_on_final' | 'fire_normal';
  status: 'running' | 'paused_suspended' | 'completed' | 'aborted' | 'failed';
  abort_requested: boolean;
  continue_despite_suspension: boolean;
  messages_scanned: number;
  inserted_count: number;
  duplicate_count: number;
  total_debited: number;
  last_balance: number | null;
  recharge_triggered_count: number;
  error_text: string | null;
  started_by: string | null;
  created_at: string;
  updated_at: string;
}

// Shape of the location-detail edit form. Defined as a fresh factory so
// editForm and originalLoadedForm can be reset independently and never share
// a reference (avoids React-state aliasing bugs).
interface EditForm {
  billing_display_name: string;
  twilio_sub_account_sid: string;
  twilio_sub_account_auth: string;
  messaging_service_sid: string;
  stripe_customer_id: string;
  billing_email: string;
  auto_recharge_enabled: boolean;
  auto_recharge_threshold: number;
  auto_recharge_amount: number;
  current_plan_id: string;
  negative_balance_threshold: number;
  is_internal: boolean;
}

function makeDefaultEditForm(): EditForm {
  return {
    billing_display_name: '',
    twilio_sub_account_sid: '',
    twilio_sub_account_auth: '',
    messaging_service_sid: '',
    stripe_customer_id: '',
    billing_email: '',
    auto_recharge_enabled: false,
    auto_recharge_threshold: 10,
    auto_recharge_amount: 50,
    current_plan_id: '',
    negative_balance_threshold: -50, // Stored as negative, displayed as positive "Credit Limit"
    is_internal: false,
  };
}

// Twilio auth tokens are exactly 32 lowercase hex characters. Front-end-only
// validation: catches the common paste mistakes (an Account SID `AC...` at 34
// chars, a truncated copy, or an autofilled password) before they reach the DB.
// A wrong token is expensive and silent — webhook HMAC validation fails, the
// event handler returns 403, and Twilio Event Streams discards the billing
// event permanently rather than retrying.
const TWILIO_AUTH_TOKEN_PATTERN = /^[0-9a-f]{32}$/;

function validateTwilioAuthToken(value: string): string | null {
  const token = value.trim();
  if (token.length === 0) return null; // Empty = "not set", handled by the backend as NULL
  if (token.toUpperCase().startsWith('AC')) {
    return 'That looks like an Account SID, not an Auth Token. The Auth Token is 32 hex characters with no "AC" prefix.';
  }
  if (token.length !== 32) {
    return `Auth Token must be exactly 32 characters (got ${token.length}).`;
  }
  if (!TWILIO_AUTH_TOKEN_PATTERN.test(token)) {
    return 'Auth Token must contain only lowercase hex characters (0-9, a-f).';
  }
  return null;
}

export default function AdminLocationBilling() {
  const initialParams = readInitialListParams();
  const [locations, setLocations] = useState<LocationBilling[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState(initialParams.search);
  const [page, setPage] = useState(initialParams.page);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState<SortBy>(initialParams.sortBy);
  const [sortDir, setSortDir] = useState<SortDir>(initialParams.sortDir);
  const [limit, setLimit] = useState<number>(initialParams.limit);
  const [pageInput, setPageInput] = useState<string>(String(initialParams.page));

  // Detail view state
  const [selectedLocation, setSelectedLocation] = useState<LocationDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Edit state.
  // Defensive: editForm + originalLoadedForm move together when a fetch
  // succeeds; on save we only PUT the diff against originalLoadedForm so
  // that no unchanged field can be accidentally NULLed by a stale form value.
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(makeDefaultEditForm());
  const [originalLoadedForm, setOriginalLoadedForm] = useState(makeDefaultEditForm());
  const [saving, setSaving] = useState(false);
  const [sidConflictError, setSidConflictError] = useState<string | null>(null);
  const [authTokenError, setAuthTokenError] = useState<string | null>(null);

  // Defensive: monotonic request ID for fetchLocationDetail. Late-arriving
  // responses with a stale ID are ignored so two near-simultaneous fetches
  // can't fight over selectedLocation/editForm.
  const locationFetchSeqRef = useRef(0);

  // Stage 1d: Twilio webhook event recovery state
  const [recoverySummary, setRecoverySummary] = useState<RecoverySummary | null>(null);
  const [loadingRecovery, setLoadingRecovery] = useState(false);
  const [showReplayConfirm, setShowReplayConfirm] = useState(false);
  const [replayInProgress, setReplayInProgress] = useState(false);
  const [replayProgress, setReplayProgress] = useState<ReplayProgress | null>(null);

  // Communications backfill state
  const [showBackfillModal, setShowBackfillModal] = useState(false);
  const [backfillDateStart, setBackfillDateStart] = useState('');
  const [backfillDateEnd, setBackfillDateEnd] = useState('');
  const [backfillIncludeVoice, setBackfillIncludeVoice] = useState(true);
  const [backfillFireMode, setBackfillFireMode] = useState<'fire_on_final' | 'fire_normal'>('fire_on_final');
  const [backfillPreview, setBackfillPreview] = useState<BackfillPreview | null>(null);
  const [backfillPreviewLoading, setBackfillPreviewLoading] = useState(false);
  const [backfillStarting, setBackfillStarting] = useState(false);
  const [backfillRuns, setBackfillRuns] = useState<BackfillRun[]>([]);
  const [backfillError, setBackfillError] = useState<string | null>(null);

  // Batch download (wind-down: export CSVs for customers who lost app access)
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [dlBatches, setDlBatches] = useState<DownloadBatchRow[]>([]);
  const [dlTotal, setDlTotal] = useState(0);
  const [dlOffset, setDlOffset] = useState(0);
  const [dlLoading, setDlLoading] = useState(false);
  const [dlError, setDlError] = useState<string | null>(null);
  // Keyed by batch id; holds the row so selections survive paging.
  const [dlSelected, setDlSelected] = useState<Map<string, DownloadBatchRow>>(new Map());
  const [dlFilter, setDlFilter] = useState<'all' | 'clean' | 'bad'>('all');
  const [dlRunning, setDlRunning] = useState(false);
  const [dlStatus, setDlStatus] = useState<Record<string, string>>({});

  // Create Stripe Customer state
  const [showStripeForm, setShowStripeForm] = useState(false);
  const [stripeCustomerName, setStripeCustomerName] = useState('');
  const [stripeCustomerEmail, setStripeCustomerEmail] = useState('');
  const [creatingStripeCustomer, setCreatingStripeCustomer] = useState(false);

  // Credit adjustment state
  const [showCreditForm, setShowCreditForm] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [creditAction, setCreditAction] = useState<'add' | 'remove'>('add');

  // Transactions state
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [showTransactions, setShowTransactions] = useState(false);

  // Plans - kept for TODO:UNIMPLEMENTED admin plan change dropdown (see Subscription Card)
  // @ts-expect-error plans read by setPlans in fetchPlans; will be used in JSX when dropdown is re-enabled
  const [plans, setPlans] = useState<Plan[]>([]);

  // Phone Lines
  const [phoneLines, setPhoneLines] = useState<PhoneLine[]>([]);
  const [loadingPhoneLines, setLoadingPhoneLines] = useState(false);
  const [syncingPhoneLines, setSyncingPhoneLines] = useState(false);
  const [showPhoneLines, setShowPhoneLines] = useState(false);
  const [phoneSyncResult, setPhoneSyncResult] = useState<string | null>(null);

  // Suspend/Unsuspend state
  const [suspending, setSuspending] = useState(false);
  const [unsuspending, setUnsuspending] = useState(false);

  // Retry subscription state
  const [retryingSubscription, setRetryingSubscription] = useState(false);
  const [retryResult, setRetryResult] = useState<{ success: boolean; message: string } | null>(null);

  // Sync subscription period state
  const [syncingPeriod, setSyncingPeriod] = useState(false);
  const [syncResult, setSyncResult] = useState<{ success: boolean; message: string } | null>(null);
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const [bulkSyncResult, setBulkSyncResult] = useState<{ success: boolean; message: string } | null>(null);

  // 30023 trust-score warnings — populated once per mount, used to render an
  // amber icon next to flagged locations in the list.
  const [warnings30023, setWarnings30023] = useState<Map<string, {
    events_today: number;
    outbound_today: number;
    pct_today: number;
  }>>(new Map());

  useEffect(() => {
    fetchLocations();
    fetchPlans();
  }, [page, search, sortBy, sortDir, limit]);

  // Sync list-view state to the URL so refresh/back/forward survive the user's
  // search, sort, page, and rows-per-page choices. We replaceState (not push)
  // so the admin sidebar's react-router state isn't polluted with intermediate
  // history entries.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const p = new URLSearchParams();
    if (search) p.set('search', search);
    if (page !== 1) p.set('page', String(page));
    if (sortBy !== DEFAULT_SORT) p.set('sort', sortBy);
    if (sortDir !== DEFAULT_DIR) p.set('dir', sortDir);
    if (limit !== DEFAULT_LIMIT) p.set('limit', String(limit));
    const qs = p.toString();
    const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    window.history.replaceState(null, '', url);
  }, [search, page, sortBy, sortDir, limit]);

  // Keep the page input box in sync when page changes via clicks.
  useEffect(() => {
    setPageInput(String(page));
  }, [page]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${import.meta.env.VITE_API_URL}/admin/30023-warning`, { credentials: 'include' })
      .then((r) => (r.ok ? (r.json() as Promise<{ success?: boolean; warnings?: Array<{
        location_id: string;
        events_today: number;
        outbound_today: number;
        pct_today: number;
      }> }>) : null))
      .then((payload) => {
        if (cancelled || !payload?.success || !payload.warnings) return;
        const next = new Map<string, { events_today: number; outbound_today: number; pct_today: number }>();
        for (const w of payload.warnings) {
          next.set(w.location_id, {
            events_today: w.events_today,
            outbound_today: w.outbound_today,
            pct_today: w.pct_today,
          });
        }
        setWarnings30023(next);
      })
      .catch(() => { /* Icon stays hidden on fetch failure */ });
    return () => { cancelled = true; };
  }, []);

  const fetchLocations = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: String(limit),
        sort: sortBy,
        dir: sortDir,
      });
      if (search) params.append('search', search);

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/billing/locations?${params}`,
        { credentials: 'include' }
      );

      const data = (await response.json()) as any;

      if (data.success) {
        setLocations(data.locations || []);
        setTotalPages(data.pagination?.totalPages || 1);
        setTotal(data.pagination?.total || 0);
      } else {
        setError(data.error || 'Failed to fetch locations');
      }
    } catch (err) {
      console.error('[LocationBilling] Error:', err);
      setError('Failed to fetch locations');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/billing/plans`, {
        credentials: 'include',
      });

      const data = (await response.json()) as any;

      if (data.success) {
        setPlans(data.plans || []);
      }
    } catch (err) {
      console.error('[LocationBilling] Plans error:', err);
    }
  };

  const fetchLocationDetail = async (locationId: string) => {
    // Tag this request so a stale earlier response can't clobber a newer view
    const mySeq = ++locationFetchSeqRef.current;
    setLoadingDetail(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/billing/locations/${locationId}`,
        { credentials: 'include' }
      );

      // Bail if a newer fetch has started since we kicked off this one
      if (mySeq !== locationFetchSeqRef.current) {
        return;
      }

      const data = (await response.json()) as any;

      if (mySeq !== locationFetchSeqRef.current) {
        return;
      }

      if (data.success) {
        // Merge payment_methods into the location object
        const locationWithPaymentMethods = {
          ...data.location,
          payment_methods: data.payment_methods || [],
        };
        setSelectedLocation(locationWithPaymentMethods);
        const loaded: EditForm = {
          billing_display_name: data.location.location_name || '',
          twilio_sub_account_sid: data.location.twilio_sub_account_sid || '',
          twilio_sub_account_auth: data.location.twilio_sub_account_auth || '',
          messaging_service_sid: data.location.messaging_service_sid || '',
          stripe_customer_id: data.location.stripe_customer_id || '',
          billing_email: data.location.billing_email || '',
          auto_recharge_enabled: data.location.auto_recharge_enabled,
          auto_recharge_threshold: data.location.auto_recharge_threshold,
          auto_recharge_amount: data.location.auto_recharge_amount,
          current_plan_id: data.location.plan_id || '',
          negative_balance_threshold: data.location.negative_balance_threshold ?? -50,
          is_internal: data.location.is_internal ?? false,
        };
        setEditForm(loaded);
        // Snapshot the pristine loaded values so handleSave can diff against
        // them and only PUT fields the user actually changed.
        setOriginalLoadedForm({ ...loaded });
        setAuthTokenError(null);
        // Pre-fill Stripe customer form with existing values
        setStripeCustomerName(data.location.location_name || '');
        setStripeCustomerEmail(data.location.billing_email || '');

        // Fetch phone lines if Twilio SID is configured
        if (data.location.twilio_sub_account_sid) {
          fetchPhoneLines(locationId);
          // Stage 1d: also check for unresolved Twilio webhook signature failures
          // for this location's current SID — surfaces the Recovery section when present
          fetchRecoverySummary(locationId);
        } else {
          setPhoneLines([]);
          setRecoverySummary(null);
        }
      } else {
        setError(data.error || 'Failed to fetch location details');
      }
    } catch (err) {
      console.error('[LocationBilling] Detail error:', err);
      setError('Failed to fetch location details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const fetchTransactions = async (locationId: string) => {
    setLoadingTransactions(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/billing/locations/${locationId}/transactions?limit=50`,
        { credentials: 'include' }
      );

      const data = (await response.json()) as any;

      if (data.success) {
        setTransactions(data.transactions || []);
      }
    } catch (err) {
      console.error('[LocationBilling] Transactions error:', err);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const fetchPhoneLines = async (locationId: string) => {
    setLoadingPhoneLines(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/billing/locations/${locationId}/phone-lines`,
        { credentials: 'include' }
      );

      const data = (await response.json()) as any;

      if (data.success) {
        setPhoneLines(data.phone_lines || []);
      }
    } catch (err) {
      console.error('[LocationBilling] Phone lines error:', err);
    } finally {
      setLoadingPhoneLines(false);
    }
  };

  // Stage 1d: fetch summary of unresolved Twilio webhook signature failures
  // for this location's current sub-account SID. Returns null when there's
  // nothing to recover (which is the common case once Phase 1 is deployed).
  const fetchRecoverySummary = async (locationId: string) => {
    setLoadingRecovery(true);
    setRecoverySummary(null);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/billing/locations/${locationId}/recovery-summary`,
        { credentials: 'include' }
      );
      const data = (await response.json()) as any;
      if (data.success) {
        setRecoverySummary(data.summary || null);
      }
    } catch (err) {
      console.error('[LocationBilling] Recovery summary error:', err);
    } finally {
      setLoadingRecovery(false);
    }
  };

  // Stage 1d: drive the replay endpoint in batches until hasMore=false or an
  // error stops us. Each backend call processes up to REPLAY_MAX_ROWS_PER_CALL
  // rows; we loop client-side so the UI shows running progress and the user
  // can see (and stop) at any time.
  const runReplay = async (locationId: string, accountSid: string) => {
    setReplayInProgress(true);
    setReplayProgress({
      totalProcessed: 0,
      totalDuplicates: 0,
      totalErrored: 0,
      totalRowsResolved: 0,
      batchesRun: 0,
      hasMore: true,
      sampleErrors: [],
    });

    const accumulated: ReplayProgress = {
      totalProcessed: 0,
      totalDuplicates: 0,
      totalErrored: 0,
      totalRowsResolved: 0,
      batchesRun: 0,
      hasMore: true,
      sampleErrors: [],
    };

    const MAX_BATCHES = 1000; // hard ceiling to prevent runaway loop if backend reports hasMore forever
    for (let i = 0; i < MAX_BATCHES; i++) {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/admin/billing/locations/${locationId}/replay-twilio-events`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ accountSid }),
          }
        );
        const data = (await response.json()) as any;
        if (!data.success) {
          accumulated.sampleErrors.push({ rowId: 'batch', error: data.error || 'Replay batch failed' });
          accumulated.hasMore = false;
          setReplayProgress({ ...accumulated });
          break;
        }
        const r = data.result;
        accumulated.totalProcessed += r.eventsProcessed || 0;
        accumulated.totalDuplicates += r.duplicatesSkipped || 0;
        accumulated.totalErrored += r.errored || 0;
        accumulated.totalRowsResolved += r.rowsMarkedResolved || 0;
        accumulated.batchesRun += 1;
        accumulated.hasMore = !!r.hasMore;
        if (Array.isArray(r.sampleErrors)) {
          for (const e of r.sampleErrors) {
            if (accumulated.sampleErrors.length < 20) accumulated.sampleErrors.push(e);
          }
        }
        setReplayProgress({ ...accumulated });

        if (!r.hasMore) break;
        if (r.rowsScanned === 0) break;
      } catch (err) {
        accumulated.sampleErrors.push({
          rowId: 'fetch',
          error: err instanceof Error ? err.message : 'Network error',
        });
        accumulated.hasMore = false;
        setReplayProgress({ ...accumulated });
        break;
      }
    }

    setReplayInProgress(false);
    // Refresh summary so the count drops to reflect what just happened
    fetchRecoverySummary(locationId);
  };

  // ---- Communications backfill ----
  const fetchBackfillRuns = async (locationId: string) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/billing/locations/${locationId}/backfill/runs`,
        { credentials: 'include' }
      );
      const data = (await response.json()) as any;
      if (data.success) setBackfillRuns(data.runs || []);
    } catch (err) {
      console.error('[LocationBilling] Backfill runs error:', err);
    }
  };

  const openBackfillModal = () => {
    setBackfillError(null);
    setBackfillPreview(null);
    // Default to a single day (yesterday, UTC) so the common case is one-day-at-a-time.
    const y = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
    setBackfillDateStart(y);
    setBackfillDateEnd(y);
    setBackfillIncludeVoice(true);
    setBackfillFireMode('fire_on_final');
    setShowBackfillModal(true);
    if (selectedLocation) fetchBackfillRuns(selectedLocation.location_id);
  };

  const fetchDownloadBatches = async (locationId: string, offset: number) => {
    setDlLoading(true);
    setDlError(null);
    try {
      const qs = new URLSearchParams({
        locationId,
        limit: String(DOWNLOAD_PAGE_SIZE),
        offset: String(offset),
      });
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/batches?${qs}`,
        { credentials: 'include' }
      );
      const data = (await response.json()) as any;
      if (data.success) {
        setDlBatches(data.batches || []);
        setDlTotal(data.totalCount || 0);
        setDlOffset(offset);
      } else {
        setDlError(data.error || 'Failed to load batches');
      }
    } catch (err) {
      setDlError(err instanceof Error ? err.message : 'Failed to load batches');
    } finally {
      setDlLoading(false);
    }
  };

  const openDownloadModal = () => {
    if (!selectedLocation) return;
    setDlBatches([]);
    setDlTotal(0);
    setDlSelected(new Map());
    setDlStatus({});
    setDlFilter('all');
    setShowDownloadModal(true);
    fetchDownloadBatches(selectedLocation.location_id, 0);
  };

  const toggleDownloadBatch = (batch: DownloadBatchRow) => {
    setDlSelected((prev) => {
      const next = new Map(prev);
      if (next.has(batch.id)) next.delete(batch.id);
      else next.set(batch.id, batch);
      return next;
    });
  };

  const toggleAllOnDownloadPage = () => {
    setDlSelected((prev) => {
      const next = new Map(prev);
      const allSelected = dlBatches.length > 0 && dlBatches.every((b) => next.has(b.id));
      for (const b of dlBatches) {
        if (allSelected) next.delete(b.id);
        else next.set(b.id, b);
      }
      return next;
    });
  };

  // Sequential on purpose: one CSV in memory at a time, and errors are
  // reported per batch without aborting the rest.
  const runBatchDownloads = async () => {
    const batches = Array.from(dlSelected.values());
    if (batches.length === 0) return;
    setDlRunning(true);
    for (const batch of batches) {
      setDlStatus((s) => ({ ...s, [batch.id]: 'Downloading…' }));
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/admin/batches/${batch.id}/download?filter=${dlFilter}`,
          { credentials: 'include' }
        );
        if (!response.ok) {
          const errorData = (await response.json().catch(() => ({}))) as { error?: string };
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `${batch.batch_name || `batch-${batch.id}`}-${dlFilter}.csv`.replace(/[<>:"/\\|?*]/g, '_');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(downloadUrl);
        setDlStatus((s) => ({ ...s, [batch.id]: 'Done' }));
      } catch (err) {
        setDlStatus((s) => ({
          ...s,
          [batch.id]: `Error: ${err instanceof Error ? err.message : 'Download failed'}`,
        }));
      }
    }
    setDlRunning(false);
  };

  const fetchBackfillPreview = async () => {
    if (!selectedLocation) return;
    setBackfillPreviewLoading(true);
    setBackfillError(null);
    setBackfillPreview(null);
    try {
      const qs = new URLSearchParams({
        date_start: backfillDateStart,
        date_end: backfillDateEnd,
        include_voice: String(backfillIncludeVoice),
      });
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/billing/locations/${selectedLocation.location_id}/backfill/preview?${qs}`,
        { credentials: 'include' }
      );
      const data = (await response.json()) as any;
      if (data.success) {
        setBackfillPreview(data.preview);
      } else {
        setBackfillError(data.error || 'Preview failed');
      }
    } catch (err) {
      setBackfillError(err instanceof Error ? err.message : 'Preview failed');
    } finally {
      setBackfillPreviewLoading(false);
    }
  };

  const startBackfill = async () => {
    if (!selectedLocation) return;
    setBackfillStarting(true);
    setBackfillError(null);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/billing/locations/${selectedLocation.location_id}/backfill/start`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            date_start: backfillDateStart,
            date_end: backfillDateEnd,
            include_voice: backfillIncludeVoice,
            fire_mode: backfillFireMode,
            accountSid: selectedLocation.twilio_sub_account_sid,
          }),
        }
      );
      const data = (await response.json()) as any;
      if (data.success) {
        setBackfillPreview(null);
        fetchBackfillRuns(selectedLocation.location_id);
      } else {
        setBackfillError(data.error || 'Failed to start backfill');
      }
    } catch (err) {
      setBackfillError(err instanceof Error ? err.message : 'Failed to start backfill');
    } finally {
      setBackfillStarting(false);
    }
  };

  const backfillControl = async (runId: string, action: 'abort' | 'continue' | 'relaunch') => {
    if (!selectedLocation) return;
    setBackfillError(null);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/billing/locations/${selectedLocation.location_id}/backfill/${runId}/control`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ action }),
        }
      );
      const data = (await response.json()) as any;
      if (!data.success) setBackfillError(data.error || 'Control action failed');
      fetchBackfillRuns(selectedLocation.location_id);
    } catch (err) {
      setBackfillError(err instanceof Error ? err.message : 'Control action failed');
    }
  };

  const syncPhoneLines = async (locationId: string) => {
    setSyncingPhoneLines(true);
    setPhoneSyncResult(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/billing/locations/${locationId}/sync-phones`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );

      const data = (await response.json()) as any;

      if (data.success) {
        setPhoneSyncResult(data.message || 'Sync completed');
        // Refresh phone lines list
        fetchPhoneLines(locationId);
      } else {
        setPhoneSyncResult(`Error: ${data.error}`);
      }
    } catch (err) {
      console.error('[LocationBilling] Sync phone lines error:', err);
      setPhoneSyncResult('Error: Failed to sync phone numbers');
    } finally {
      setSyncingPhoneLines(false);
    }
  };

  const handleSuspend = async () => {
    if (!selectedLocation) return;
    setSuspending(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/billing/locations/${selectedLocation.location_id}/suspend`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );

      const data = (await response.json()) as any;

      if (data.success) {
        fetchLocationDetail(selectedLocation.location_id);
      } else {
        setError(data.error || 'Failed to suspend account');
      }
    } catch (err) {
      console.error('[LocationBilling] Suspend error:', err);
      setError('Failed to suspend account');
    } finally {
      setSuspending(false);
    }
  };

  const handleUnsuspend = async () => {
    if (!selectedLocation) return;
    setUnsuspending(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/billing/locations/${selectedLocation.location_id}/unsuspend`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );

      const data = (await response.json()) as any;

      if (data.success) {
        // Refresh detail view
        fetchLocationDetail(selectedLocation.location_id);
      } else {
        setError(data.error || 'Failed to unsuspend account');
      }
    } catch (err) {
      console.error('[LocationBilling] Unsuspend error:', err);
      setError('Failed to unsuspend account');
    } finally {
      setUnsuspending(false);
    }
  };

  const handleRetrySubscription = async () => {
    if (!selectedLocation) return;
    if (!confirm('Retry subscription payment using cards on file?')) return;
    setRetryingSubscription(true);
    setRetryResult(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/billing/locations/${selectedLocation.location_id}/retry-subscription`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );
      const data = await response.json() as { success: boolean; message?: string; error?: string };

      if (data.success) {
        setRetryResult({ success: true, message: data.message || 'Payment succeeded' });
        fetchLocationDetail(selectedLocation.location_id);
      } else {
        setRetryResult({ success: false, message: data.error || 'Retry failed' });
      }
    } catch (err) {
      console.error('[LocationBilling] Retry subscription error:', err);
      setRetryResult({ success: false, message: 'Failed to retry subscription payment' });
    } finally {
      setRetryingSubscription(false);
    }
  };

  const handleSyncSubscriptionPeriod = async () => {
    if (!selectedLocation) return;
    if (!confirm('Sync billing cycle dates from Stripe? This overwrites local billing_cycle_start and billing_cycle_end with Stripe\'s authoritative values.')) return;
    setSyncingPeriod(true);
    setSyncResult(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/billing/locations/${selectedLocation.location_id}/sync-subscription-period`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({}),
        }
      );
      const data = await response.json() as {
        success: boolean;
        results?: Array<{
          location_id: string;
          status: string;
          before_subscription_id?: string | null;
          after_subscription_id?: string | null;
          before_cycle_end?: string | null;
          after_cycle_end?: string | null;
          error?: string;
        }>;
        error?: string;
      };

      if (data.success && data.results?.[0]) {
        const r = data.results[0];
        if (r.status === 'synced') {
          const beforeEnd = r.before_cycle_end ? new Date(r.before_cycle_end).toLocaleString() : 'null';
          const afterEnd = r.after_cycle_end ? new Date(r.after_cycle_end).toLocaleString() : 'null';
          const subChanged = r.before_subscription_id !== r.after_subscription_id;
          const subInfo = subChanged
            ? ` Subscription corrected: ${r.before_subscription_id || 'none'} → ${r.after_subscription_id}.`
            : '';
          setSyncResult({ success: true, message: `Synced.${subInfo} billing_cycle_end: ${beforeEnd} → ${afterEnd}` });
          fetchLocationDetail(selectedLocation.location_id);
        } else {
          setSyncResult({ success: false, message: `${r.status}${r.error ? `: ${r.error}` : ''}` });
        }
      } else {
        setSyncResult({ success: false, message: data.error || 'Sync failed' });
      }
    } catch (err) {
      console.error('[LocationBilling] Sync subscription period error:', err);
      setSyncResult({ success: false, message: 'Failed to sync subscription period' });
    } finally {
      setSyncingPeriod(false);
    }
  };

  const handleBulkSyncSubscriptionPeriods = async () => {
    if (!confirm('Sync billing cycle dates from Stripe for ALL locations with active subscriptions? This overwrites every local billing_cycle_start and billing_cycle_end with Stripe\'s authoritative values. This cannot be undone.')) return;
    setBulkSyncing(true);
    setBulkSyncResult(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/billing/locations/_/sync-subscription-period`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ all: true }),
        }
      );
      const data = await response.json() as {
        success: boolean;
        count?: number;
        results?: Array<{ status: string }>;
        warning?: string;
        error?: string;
      };

      if (data.success) {
        const synced = data.results?.filter((r) => r.status === 'synced').length || 0;
        const skipped = (data.count || 0) - synced;
        const warn = data.warning ? ` (${data.warning})` : '';
        setBulkSyncResult({
          success: true,
          message: `Synced ${synced} of ${data.count || 0} locations${skipped > 0 ? `, ${skipped} skipped/failed` : ''}${warn}`,
        });
      } else {
        setBulkSyncResult({ success: false, message: data.error || 'Bulk sync failed' });
      }
    } catch (err) {
      console.error('[LocationBilling] Bulk sync subscription period error:', err);
      setBulkSyncResult({ success: false, message: 'Failed to bulk-sync subscription periods' });
    } finally {
      setBulkSyncing(false);
    }
  };

  const handleSave = async () => {
    if (!selectedLocation) return;
    setSidConflictError(null);

    // Trim before validating and saving. A trailing newline or space from a
    // console copy is invisible in the field but breaks webhook HMAC validation.
    const trimmedAuth = editForm.twilio_sub_account_auth.trim();
    const authError = validateTwilioAuthToken(trimmedAuth);
    if (authError) {
      setAuthTokenError(authError);
      return;
    }
    setAuthTokenError(null);

    // Snapshot the form with the trimmed token so the diff below and the PUT
    // body both see the trimmed value. setEditForm is async and would not have
    // applied in time, so we never read editForm.twilio_sub_account_auth again.
    const formToSave: EditForm = { ...editForm, twilio_sub_account_auth: trimmedAuth };
    if (trimmedAuth !== editForm.twilio_sub_account_auth) {
      setEditForm(formToSave);
    }

    setSaving(true);

    // Check if Twilio SID is being set for the first time (for auto-sync)
    const isNewTwilioSid =
      !selectedLocation.twilio_sub_account_sid &&
      formToSave.twilio_sub_account_sid &&
      formToSave.twilio_sub_account_auth;

    // Defensive: diff formToSave against originalLoadedForm and only PUT the
    // fields the user actually changed. The backend's `if (field in body)`
    // semantics means unchanged fields are left untouched in the DB. This
    // protects against the stale-form bug that wiped Kassis's
    // messaging_service_sid on 2026-05-28 (form held empty string for an
    // untouched field, backend wrote NULL).
    const changedFields: Partial<EditForm> = {};
    let hasChanges = false;
    (Object.keys(formToSave) as Array<keyof EditForm>).forEach((key) => {
      if (formToSave[key] !== originalLoadedForm[key]) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (changedFields as any)[key] = formToSave[key];
        hasChanges = true;
      }
    });

    if (!hasChanges) {
      // Nothing changed — close edit mode without a network call
      setEditing(false);
      setSaving(false);
      return;
    }

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/billing/locations/${selectedLocation.location_id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(changedFields),
        }
      );

      const data = (await response.json()) as any;

      // Stage 1b: 409 SID conflict surfaces inline next to the SID input.
      // Other backend errors continue to use the generic alert flow.
      if (response.status === 409 && data.error === 'sid_conflict') {
        setSidConflictError(data.message || 'Twilio sub-account SID is already in use by another location.');
        return;
      }

      if (data.success) {
        setEditing(false);
        fetchLocationDetail(selectedLocation.location_id);
        fetchLocations();

        // Auto-sync phone numbers when Twilio SID is first saved
        if (isNewTwilioSid) {
          syncPhoneLines(selectedLocation.location_id);
        }
      } else {
        alert(data.error || 'Failed to update');
      }
    } catch (err) {
      console.error('[LocationBilling] Save error:', err);
      alert('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleCreditAdjustment = async () => {
    if (!selectedLocation || !creditAmount || !creditReason) return;
    setSaving(true);

    const amount = parseFloat(creditAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid amount');
      setSaving(false);
      return;
    }

    try {
      const endpoint =
        creditAction === 'add'
          ? `${import.meta.env.VITE_API_URL}/admin/billing/locations/${selectedLocation.location_id}/credit`
          : `${import.meta.env.VITE_API_URL}/admin/billing/locations/${selectedLocation.location_id}/credit`;

      const response = await fetch(endpoint, {
        method: creditAction === 'add' ? 'POST' : 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ amount, reason: creditReason }),
      });

      const data = (await response.json()) as any;

      if (data.success) {
        setShowCreditForm(false);
        setCreditAmount('');
        setCreditReason('');
        fetchLocationDetail(selectedLocation.location_id);
        if (showTransactions) {
          fetchTransactions(selectedLocation.location_id);
        }
      } else {
        alert(data.error || 'Failed to adjust credit');
      }
    } catch (err) {
      console.error('[LocationBilling] Credit error:', err);
      alert('Failed to adjust credit');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateStripeCustomer = async () => {
    if (!selectedLocation || !stripeCustomerName.trim()) return;
    setCreatingStripeCustomer(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/billing/locations/${selectedLocation.location_id}/create-stripe-customer`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            name: stripeCustomerName.trim(),
            email: stripeCustomerEmail.trim() || undefined,
          }),
        }
      );

      const data = (await response.json()) as any;

      if (data.success) {
        setShowStripeForm(false);
        setStripeCustomerName('');
        setStripeCustomerEmail('');
        // Refresh location details to show new Stripe customer ID
        fetchLocationDetail(selectedLocation.location_id);
      } else {
        alert(data.error || 'Failed to create Stripe customer');
      }
    } catch (err) {
      console.error('[LocationBilling] Create Stripe customer error:', err);
      alert('Failed to create Stripe customer');
    } finally {
      setCreatingStripeCustomer(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateShort = (date: string) =>
    new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  // ============================================================
  // List view: sort + pagination helpers
  // ============================================================
  const goToPage = (n: number) => {
    const tp = Math.max(totalPages || 1, 1);
    const clamped = Math.max(1, Math.min(tp, Math.floor(n) || 1));
    setPage(clamped);
  };

  const handleSort = (column: SortBy) => {
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      // Sensible default direction per column: name=asc, wallet/created=desc
      setSortBy(column);
      setSortDir(column === 'name' ? 'asc' : 'desc');
    }
    setPage(1);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const submitPageInput = () => {
    const n = parseInt(pageInput, 10);
    if (Number.isFinite(n) && n >= 1) {
      goToPage(n);
    } else {
      setPageInput(String(page));
    }
  };

  const renderSortHeader = (
    column: SortBy,
    label: string,
    align: 'left' | 'right' = 'left'
  ) => {
    const active = sortBy === column;
    const Icon = !active ? ArrowUpDown : sortDir === 'asc' ? ArrowUp : ArrowDown;
    return (
      <th
        onClick={() => handleSort(column)}
        className={`py-3 px-4 text-sm font-medium text-gray-600 cursor-pointer select-none hover:bg-gray-100 ${
          align === 'right' ? 'text-right' : 'text-left'
        }`}
        aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <span
          className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}
        >
          {label}
          <Icon className={`w-3.5 h-3.5 ${active ? 'text-blue-600' : 'text-gray-400'}`} />
        </span>
      </th>
    );
  };

  const renderPaginationBar = (position: 'top' | 'bottom') => {
    const tp = Math.max(totalPages || 1, 1);
    return (
      <div
        className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 ${
          position === 'top'
            ? 'bg-gray-50 border-b border-gray-200'
            : 'bg-gray-50 border-t border-gray-200'
        }`}
      >
        <div className="flex items-center gap-3 text-sm text-gray-600">
          <span>
            {total > 0
              ? `Showing ${(page - 1) * limit + 1} to ${Math.min(page * limit, total)} of ${total} locations`
              : 'No locations'}
          </span>
          <span className="text-gray-300">|</span>
          <label className="flex items-center gap-2">
            Rows:
            <select
              value={limit}
              onChange={(e) => handleLimitChange(parseInt(e.target.value, 10))}
              className="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
            >
              {LIMIT_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => goToPage(1)}
            disabled={page <= 1}
            title="First page"
            aria-label="First page"
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronsLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => goToPage(page - 1)}
            disabled={page <= 1}
            title="Previous page"
            aria-label="Previous page"
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-sm text-gray-600 px-2 flex items-center gap-1">
            Page
            <input
              type="text"
              inputMode="numeric"
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value.replace(/[^0-9]/g, ''))}
              onBlur={submitPageInput}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  (e.currentTarget as HTMLInputElement).blur();
                }
              }}
              className="w-12 px-1.5 py-1 text-center border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
              aria-label="Page number"
            />
            of {tp}
          </span>
          <button
            onClick={() => goToPage(page + 1)}
            disabled={page >= tp}
            title="Next page"
            aria-label="Next page"
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <button
            onClick={() => goToPage(tp)}
            disabled={page >= tp}
            title="Last page"
            aria-label="Last page"
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronsRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  };

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case 'topup':
      case 'admin_credit':
        return 'text-green-600 bg-green-50';
      case 'sms_charge':
      case 'mms_charge':
      case 'voice_charge':
      case 'admin_debit':
        return 'text-red-600 bg-red-50';
      case 'refund':
        return 'text-blue-600 bg-blue-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getSubscriptionStatusBadge = (status: string | null) => {
    if (!status || status === 'No Subscription') {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
          No Subscription
        </span>
      );
    }

    // Handle derived statuses with dates
    if (status.startsWith('Renews on') || status === 'Active') {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          {status}
        </span>
      );
    }

    if (status.startsWith('Canceling on')) {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
          <AlertCircle className="w-3 h-3 mr-1" />
          {status}
        </span>
      );
    }

    if (status.startsWith('Past Due')) {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
          <AlertCircle className="w-3 h-3 mr-1" />
          {status}
        </span>
      );
    }

    if (status.startsWith('Downgrading')) {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
          <Clock className="w-3 h-3 mr-1" />
          {status}
        </span>
      );
    }

    // Fallback for any other status
    return (
      <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
        {status}
      </span>
    );
  };

  // Detail View
  if (selectedLocation) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                setSelectedLocation(null);
                setEditing(false);
                setShowTransactions(false);
                setShowPhoneLines(false);
                setPhoneLines([]);
                setPhoneSyncResult(null);
                // Defensive: clear form state so the next location entered
                // gets a clean slate instead of carrying stale values forward
                setEditForm(makeDefaultEditForm());
                setOriginalLoadedForm(makeDefaultEditForm());
                setSidConflictError(null);
                setAuthTokenError(null);
                setRecoverySummary(null);
              }}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                {selectedLocation.location_name || selectedLocation.location_id}
                {selectedLocation.is_internal && (
                  <span className="inline-flex items-center px-2 py-1 rounded text-sm font-medium bg-purple-100 text-purple-800">
                    Internal User
                  </span>
                )}
                {selectedLocation.billing_suspended_at && (
                  // Admin suspension wins: top-ups can never lift it, only Unsuspend can.
                  // Otherwise plan non-payment, otherwise the wallet balance.
                  selectedLocation.admin_suspended_at ? (
                    <span
                      className="inline-flex items-center px-2 py-1 rounded text-sm font-medium bg-red-100 text-red-800"
                      title="Suspended by an admin. Wallet top-ups will not lift it; only Unsuspend Account will. Top-ups only collect a negative balance up to $0."
                    >
                      Suspended · Admin
                    </span>
                  ) : selectedLocation.plan_suspended_at ? (
                    <span
                      className="inline-flex items-center px-2 py-1 rounded text-sm font-medium bg-orange-100 text-orange-800"
                      title="Suspended for repeated plan payment failure. Lifts when the plan invoice is paid and the wallet is above its credit limit."
                    >
                      Suspended · Plan Unpaid
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center px-2 py-1 rounded text-sm font-medium bg-amber-100 text-amber-800"
                      title="Suspended for negative balance. Lifts automatically when a top-up brings the balance above the credit limit."
                    >
                      Suspended · Balance
                    </span>
                  )
                )}
              </h1>
              <p className="text-gray-500 text-sm">
                <span className="font-mono">{selectedLocation.location_id}</span>
                {!selectedLocation.has_billing_config && (
                  <span className="ml-2 text-yellow-600">(Not configured)</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {selectedLocation.billing_suspended_at ? (
              <button
                onClick={handleUnsuspend}
                disabled={unsuspending}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <CheckCircle className="w-4 h-4" />
                <span>{unsuspending ? 'Unsuspending...' : 'Unsuspend Account'}</span>
              </button>
            ) : (
              <button
                onClick={handleSuspend}
                disabled={suspending}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                <AlertCircle className="w-4 h-4" />
                <span>{suspending ? 'Suspending...' : 'Suspend Account'}</span>
              </button>
            )}
            {!editing ? (
              <button
                onClick={() => {
                  setAuthTokenError(null);
                  setSidConflictError(null);
                  setEditing(true);
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Edit2 className="w-4 h-4" />
                <span>Edit</span>
              </button>
            ) : (
              <>
                <button
                  onClick={() => {
                    // Discard unsaved edits along with their validation errors,
                    // so a rejected token can't linger in the form.
                    setEditForm(originalLoadedForm);
                    setAuthTokenError(null);
                    setSidConflictError(null);
                    setEditing(false);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !!authTokenError}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  <span>{saving ? 'Saving...' : 'Save'}</span>
                </button>
              </>
            )}
          </div>
        </div>

        {loadingDetail ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Wallet Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Wallet className="w-5 h-5 mr-2 text-green-600" />
                  Wallet Balance
                </h2>
                <button
                  onClick={() => setShowCreditForm(!showCreditForm)}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Adjust Credit
                </button>
              </div>

              <p className="text-4xl font-bold text-gray-900 mb-4">
                {formatCurrency(selectedLocation.wallet_balance)}
              </p>

              {showCreditForm && (
                <div className="border-t border-gray-200 pt-4 mt-4 space-y-3">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCreditAction('add')}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium ${
                        creditAction === 'add'
                          ? 'bg-green-100 text-green-700 border-2 border-green-500'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      <Plus className="w-4 h-4 inline mr-1" />
                      Add Credit
                    </button>
                    <button
                      onClick={() => setCreditAction('remove')}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium ${
                        creditAction === 'remove'
                          ? 'bg-red-100 text-red-700 border-2 border-red-500'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      <Minus className="w-4 h-4 inline mr-1" />
                      Remove Credit
                    </button>
                  </div>
                  <input
                    type="number"
                    value={creditAmount}
                    onChange={(e) => setCreditAmount(e.target.value)}
                    placeholder="Amount"
                    step="0.01"
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <input
                    type="text"
                    value={creditReason}
                    onChange={(e) => setCreditReason(e.target.value)}
                    placeholder="Reason for adjustment"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <button
                    onClick={handleCreditAdjustment}
                    disabled={saving || !creditAmount || !creditReason}
                    className={`w-full py-2 rounded-lg text-white font-medium disabled:opacity-50 ${
                      creditAction === 'add' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                    }`}
                  >
                    {saving ? 'Processing...' : creditAction === 'add' ? 'Add Credit' : 'Remove Credit'}
                  </button>
                </div>
              )}

              <div className="border-t border-gray-200 pt-4 mt-4">
                <button
                  onClick={() => {
                    setShowTransactions(!showTransactions);
                    if (!showTransactions) {
                      fetchTransactions(selectedLocation.location_id);
                    }
                  }}
                  className="flex items-center text-sm text-blue-600 hover:text-blue-700"
                >
                  <History className="w-4 h-4 mr-1" />
                  {showTransactions ? 'Hide' : 'View'} Transaction History
                </button>
              </div>
            </div>

            {/* Subscription Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
                <CreditCard className="w-5 h-5 mr-2 text-blue-600" />
                Subscription
              </h2>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Status</span>
                  {getSubscriptionStatusBadge(selectedLocation.subscription_status)}
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Plan</span>
                  {/* TODO:UNIMPLEMENTED - Admin plan change dropdown removed because it only updated the
                     database without making corresponding Stripe subscription changes (no proration,
                     no billing cycle update, no subscription_events log). To re-enable, wire up the
                     same Stripe subscription change logic used by the user-facing POST
                     /billing/:locationId/change-plan endpoint (see user-billing.ts). The plans list
                     is already fetched and available in the `plans` state variable. */}
                  <span className="font-medium">{selectedLocation.plan_name || 'None'}</span>
                </div>
                {selectedLocation.current_period_end && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Current Period Ends</span>
                    <span className={`font-medium ${new Date(selectedLocation.current_period_end) < new Date() ? 'text-red-600' : ''}`}>
                      {new Date(selectedLocation.current_period_end).toLocaleDateString()}
                      {new Date(selectedLocation.current_period_end) < new Date() && ' (PAST DUE)'}
                    </span>
                  </div>
                )}

                {/* Retry Subscription Payment button - show when period is past due */}
                {selectedLocation.stripe_subscription_id && selectedLocation.current_period_end &&
                  new Date(selectedLocation.current_period_end) < new Date() && (
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <button
                      onClick={handleRetrySubscription}
                      disabled={retryingSubscription}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      <CreditCard className="w-4 h-4" />
                      <span>{retryingSubscription ? 'Retrying Payment...' : 'Retry Subscription Payment'}</span>
                    </button>
                    {retryResult && (
                      <div className={`mt-2 text-sm p-2 rounded ${retryResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                        {retryResult.message}
                      </div>
                    )}
                  </div>
                )}

                {/* Sync Subscription Period from Stripe (recovers local cycle dates that drifted from Stripe) */}
                {selectedLocation.stripe_subscription_id && (
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <button
                      onClick={handleSyncSubscriptionPeriod}
                      disabled={syncingPeriod}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                    >
                      <RefreshCw className={`w-4 h-4 ${syncingPeriod ? 'animate-spin' : ''}`} />
                      <span>{syncingPeriod ? 'Syncing from Stripe...' : 'Sync Period from Stripe'}</span>
                    </button>
                    {syncResult && (
                      <div className={`mt-2 text-sm p-2 rounded ${syncResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                        {syncResult.message}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Auto-Recharge Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Auto-Recharge Settings</h2>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Enabled</span>
                  {editing ? (
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editForm.auto_recharge_enabled}
                        onChange={(e) =>
                          setEditForm({ ...editForm, auto_recharge_enabled: e.target.checked })
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                    </label>
                  ) : (
                    <span
                      className={`px-2 py-1 rounded text-sm font-medium ${
                        selectedLocation.auto_recharge_enabled
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {selectedLocation.auto_recharge_enabled ? 'Yes' : 'No'}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Threshold</span>
                  {editing ? (
                    <input
                      type="number"
                      value={editForm.auto_recharge_threshold}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          auto_recharge_threshold: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-24 px-2 py-1 border border-gray-300 rounded text-right"
                      step="1"
                      min="0"
                    />
                  ) : (
                    <span className="font-medium">
                      {formatCurrency(selectedLocation.auto_recharge_threshold)}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Recharge Amount</span>
                  {editing ? (
                    <input
                      type="number"
                      value={editForm.auto_recharge_amount}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          auto_recharge_amount: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-24 px-2 py-1 border border-gray-300 rounded text-right"
                      step="5"
                      min="5"
                    />
                  ) : (
                    <span className="font-medium">
                      {formatCurrency(selectedLocation.auto_recharge_amount)}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-gray-100 mt-2">
                  <div>
                    <span className="text-gray-600">Credit Limit</span>
                    <p className="text-xs text-gray-400">How far past $0 they can go</p>
                  </div>
                  {editing ? (
                    <input
                      type="number"
                      value={Math.abs(editForm.negative_balance_threshold)}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          negative_balance_threshold: -Math.abs(parseFloat(e.target.value) || 0),
                        })
                      }
                      className="w-24 px-2 py-1 border border-gray-300 rounded text-right"
                      step="10"
                      min="0"
                    />
                  ) : (
                    <span className="font-medium">
                      {formatCurrency(Math.abs(selectedLocation.negative_balance_threshold))}
                    </span>
                  )}
                </div>

                {/* Internal User Toggle */}
                <div className="flex items-center justify-between pt-2 border-t border-gray-100 mt-2">
                  <div>
                    <span className="text-gray-600">Internal User</span>
                    <p className="text-xs text-gray-400">Bypasses billing checks (no plan/payment required)</p>
                  </div>
                  {editing ? (
                    <button
                      type="button"
                      onClick={() => setEditForm({ ...editForm, is_internal: !editForm.is_internal })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        editForm.is_internal ? 'bg-purple-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          editForm.is_internal ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  ) : (
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      selectedLocation.is_internal
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {selectedLocation.is_internal ? 'Yes' : 'No'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Integration Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
                <Building2 className="w-5 h-5 mr-2 text-purple-600" />
                Integration IDs
              </h2>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Business Name</label>
                  {editing ? (
                    <input
                      type="text"
                      value={editForm.billing_display_name}
                      onChange={(e) =>
                        setEditForm({ ...editForm, billing_display_name: e.target.value })
                      }
                      placeholder="Business or location name"
                      className="w-full text-sm px-3 py-2 border border-gray-300 rounded"
                    />
                  ) : (
                    <p className="text-sm bg-gray-50 px-3 py-2 rounded">
                      {selectedLocation.location_name || 'Not set'}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Used for Stripe customer name and display purposes</p>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Billing Email</label>
                  {editing ? (
                    <input
                      type="email"
                      value={editForm.billing_email}
                      onChange={(e) =>
                        setEditForm({ ...editForm, billing_email: e.target.value })
                      }
                      placeholder="billing@example.com"
                      className="w-full text-sm px-3 py-2 border border-gray-300 rounded"
                    />
                  ) : (
                    <p className="text-sm bg-gray-50 px-3 py-2 rounded">
                      {selectedLocation.billing_email || 'Not set'}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Used for payment notifications and Stripe customer email</p>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Stripe Customer ID</label>
                  {editing ? (
                    <input
                      type="text"
                      value={editForm.stripe_customer_id}
                      onChange={(e) =>
                        setEditForm({ ...editForm, stripe_customer_id: e.target.value })
                      }
                      placeholder="cus_..."
                      className="w-full font-mono text-sm px-3 py-2 border border-gray-300 rounded"
                    />
                  ) : (
                    <div className="flex items-center space-x-2">
                      <p className="font-mono text-sm bg-gray-50 px-3 py-2 rounded flex-1">
                        {selectedLocation.stripe_customer_id || 'Not set'}
                      </p>
                      {!selectedLocation.stripe_customer_id && (
                        <button
                          onClick={() => setShowStripeForm(true)}
                          className="px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Create
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Create Stripe Customer Form */}
                {showStripeForm && !selectedLocation.stripe_customer_id && (
                  <div className="border border-blue-200 bg-blue-50 rounded-lg p-4 space-y-3">
                    <h3 className="text-sm font-medium text-blue-900">Create Stripe Customer</h3>
                    <input
                      type="text"
                      value={stripeCustomerName}
                      onChange={(e) => setStripeCustomerName(e.target.value)}
                      placeholder="Customer Name"
                      className="w-full text-sm px-3 py-2 border border-gray-300 rounded"
                    />
                    <input
                      type="email"
                      value={stripeCustomerEmail}
                      onChange={(e) => setStripeCustomerEmail(e.target.value)}
                      placeholder="Email (optional)"
                      className="w-full text-sm px-3 py-2 border border-gray-300 rounded"
                    />
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setShowStripeForm(false);
                          setStripeCustomerName('');
                          setStripeCustomerEmail('');
                        }}
                        className="flex-1 px-3 py-2 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCreateStripeCustomer}
                        disabled={creatingStripeCustomer || !stripeCustomerName.trim()}
                        className="flex-1 px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        {creatingStripeCustomer ? 'Creating...' : 'Create Customer'}
                      </button>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Twilio Sub-Account SID</label>
                  {editing ? (
                    <>
                      <input
                        type="text"
                        value={editForm.twilio_sub_account_sid}
                        onChange={(e) => {
                          setEditForm({ ...editForm, twilio_sub_account_sid: e.target.value });
                          if (sidConflictError) setSidConflictError(null);
                        }}
                        placeholder="AC..."
                        className={`w-full font-mono text-sm px-3 py-2 border rounded ${
                          sidConflictError ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {sidConflictError && (
                        <p className="mt-1 text-sm text-red-600">{sidConflictError}</p>
                      )}
                    </>
                  ) : (
                    <p className="font-mono text-sm bg-gray-50 px-3 py-2 rounded">
                      {selectedLocation.twilio_sub_account_sid || 'Not set'}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Twilio Auth Token</label>
                  {editing ? (
                    <>
                      <input
                        type="text"
                        autoComplete="off"
                        spellCheck={false}
                        value={editForm.twilio_sub_account_auth}
                        onChange={(e) => {
                          const value = e.target.value;
                          setEditForm({ ...editForm, twilio_sub_account_auth: value });
                          setAuthTokenError(validateTwilioAuthToken(value));
                        }}
                        placeholder="32 hex characters"
                        className={`w-full font-mono text-sm px-3 py-2 border rounded ${
                          authTokenError ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {authTokenError && (
                        <p className="mt-1 text-sm text-red-600">{authTokenError}</p>
                      )}
                    </>
                  ) : (
                    <p className="font-mono text-sm bg-gray-50 px-3 py-2 rounded break-all">
                      {selectedLocation.twilio_sub_account_auth || (
                        <span className="text-gray-400">Not set</span>
                      )}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Messaging Service SID
                    <span className="text-xs text-gray-400 ml-1">(for A2P campaign)</span>
                  </label>
                  {editing ? (
                    <input
                      type="text"
                      value={editForm.messaging_service_sid}
                      onChange={(e) =>
                        setEditForm({ ...editForm, messaging_service_sid: e.target.value })
                      }
                      placeholder="MG..."
                      className="w-full font-mono text-sm px-3 py-2 border border-gray-300 rounded"
                    />
                  ) : (
                    <p className="font-mono text-sm bg-gray-50 px-3 py-2 rounded">
                      {selectedLocation.messaging_service_sid || (
                        <span className="text-gray-400">Not set</span>
                      )}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    New phones will be auto-added to this Messaging Service for A2P compliance
                  </p>
                </div>

                {/* Batch CSV download (wind-down: customers without app access) */}
                {!editing && (
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Download className="w-4 h-4 text-indigo-600" />
                        <span className="text-sm font-medium text-gray-700">List Cleaner Batches</span>
                      </div>
                      <button
                        onClick={openDownloadModal}
                        className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700"
                      >
                        Download Batches…
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Export processed-contact CSVs on the customer's behalf.
                    </p>
                  </div>
                )}

                {/* Communications Backfill (Twilio Event Streams gap recovery) */}
                {!editing && selectedLocation.twilio_sub_account_sid && (
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <RefreshCw className="w-4 h-4 text-indigo-600" />
                        <span className="text-sm font-medium text-gray-700">Communications Backfill</span>
                      </div>
                      <button
                        onClick={openBackfillModal}
                        className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700"
                      >
                        Backfill from Twilio…
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Re-pull &amp; bill SMS/MMS/Voice that Twilio recorded but the sync missed (e.g. Event
                      Streams was off). Free to read; billed at current rates. Safe to re-run — duplicates are skipped.
                    </p>
                  </div>
                )}

                {/* Stage 1d: Twilio Webhook Event Recovery */}
                {!editing && recoverySummary && recoverySummary.unresolvedCount > 0 && (
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                        <span className="text-sm font-medium text-gray-700">Recovery</span>
                        <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-800 rounded-full">
                          {recoverySummary.unresolvedCount.toLocaleString()} pending
                        </span>
                      </div>
                      <button
                        onClick={() => setShowReplayConfirm(true)}
                        disabled={loadingRecovery || replayInProgress}
                        className="px-3 py-1.5 text-xs bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
                      >
                        Replay events
                      </button>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm space-y-1">
                      <p className="text-amber-900">
                        Twilio sent <strong>{recoverySummary.unresolvedCount.toLocaleString()}</strong> events
                        for this sub-account that were rejected at the webhook door and not billed.
                        They can be replayed safely now that the SID is correctly configured.
                      </p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-amber-800 mt-2">
                        <div>
                          <span className="text-amber-700">Time range:</span>{' '}
                          {recoverySummary.oldestSeen
                            ? `${new Date(recoverySummary.oldestSeen).toLocaleString()} → ${
                                recoverySummary.newestSeen
                                  ? new Date(recoverySummary.newestSeen).toLocaleString()
                                  : 'now'
                              }`
                            : 'unknown'}
                        </div>
                        <div>
                          <span className="text-amber-700">Estimated wallet impact:</span>{' '}
                          {recoverySummary.isInternal
                            ? 'Internal user (no charge)'
                            : `$${recoverySummary.estimatedWalletImpact.toFixed(2)} ${
                                recoverySummary.breakdownIsEstimate ? '(approximate)' : ''
                              }`}
                        </div>
                      </div>
                      {Object.keys(recoverySummary.eventTypeBreakdown).length > 0 && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-xs text-amber-700 hover:text-amber-900">
                            Event breakdown
                          </summary>
                          <ul className="mt-1 ml-4 text-xs text-amber-800 space-y-0.5">
                            {Object.entries(recoverySummary.eventTypeBreakdown)
                              .sort((a, b) => b[1] - a[1])
                              .map(([type, count]) => (
                                <li key={type}>
                                  <span className="font-mono">{type}</span>: {count.toLocaleString()}
                                </li>
                              ))}
                          </ul>
                        </details>
                      )}
                    </div>
                    {replayProgress && (replayInProgress || replayProgress.batchesRun > 0) && (
                      <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                        <div className="flex items-center space-x-2 mb-2">
                          {replayInProgress ? (
                            <RefreshCw className="w-4 h-4 text-blue-600 animate-spin" />
                          ) : (
                            <CheckCircle className="w-4 h-4 text-blue-600" />
                          )}
                          <span className="font-medium text-blue-900">
                            {replayInProgress
                              ? `Replay running (batch ${replayProgress.batchesRun}…)`
                              : 'Replay complete'}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-blue-800">
                          <div>Processed: {replayProgress.totalProcessed.toLocaleString()}</div>
                          <div>Duplicates skipped: {replayProgress.totalDuplicates.toLocaleString()}</div>
                          <div>Errored: {replayProgress.totalErrored.toLocaleString()}</div>
                          <div>Rows resolved: {replayProgress.totalRowsResolved.toLocaleString()}</div>
                        </div>
                        {replayProgress.sampleErrors.length > 0 && (
                          <details className="mt-2">
                            <summary className="cursor-pointer text-xs text-blue-700 hover:text-blue-900">
                              First {replayProgress.sampleErrors.length} error(s)
                            </summary>
                            <ul className="mt-1 ml-4 text-xs text-blue-800 space-y-0.5 font-mono">
                              {replayProgress.sampleErrors.map((e, i) => (
                                <li key={i} className="break-all">
                                  <span className="text-blue-600">{e.rowId.substring(0, 8)}</span>: {e.error}
                                </li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Phone Lines Section */}
                {selectedLocation.twilio_sub_account_sid && !editing && (
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <Phone className="w-4 h-4 text-gray-600" />
                        <span className="text-sm font-medium text-gray-700">Phone Lines</span>
                        {phoneLines.length > 0 && (
                          <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                            {phoneLines.length} {phoneLines.length === 1 ? 'line' : 'lines'}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => syncPhoneLines(selectedLocation.location_id)}
                          disabled={syncingPhoneLines}
                          className="flex items-center px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                          <RefreshCw className={`w-3 h-3 mr-1 ${syncingPhoneLines ? 'animate-spin' : ''}`} />
                          {syncingPhoneLines ? 'Syncing...' : 'Sync'}
                        </button>
                        {phoneLines.length > 0 && (
                          <button
                            onClick={() => {
                              setShowPhoneLines(!showPhoneLines);
                              if (!showPhoneLines && phoneLines.length === 0) {
                                fetchPhoneLines(selectedLocation.location_id);
                              }
                            }}
                            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                          >
                            {showPhoneLines ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Sync Result Message */}
                    {phoneSyncResult && (
                      <div
                        className={`text-xs px-3 py-2 rounded mb-3 ${
                          phoneSyncResult.startsWith('Error')
                            ? 'bg-red-50 text-red-700'
                            : 'bg-green-50 text-green-700'
                        }`}
                      >
                        {phoneSyncResult}
                      </div>
                    )}

                    {/* Phone Lines List */}
                    {showPhoneLines && (
                      <div className="space-y-2">
                        {loadingPhoneLines ? (
                          <div className="flex items-center justify-center py-4">
                            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        ) : phoneLines.length === 0 ? (
                          <p className="text-xs text-gray-500 text-center py-3">
                            No phone lines synced. Click Sync to fetch from Twilio.
                          </p>
                        ) : (
                          <div className="max-h-64 overflow-y-auto">
                            <table className="w-full text-xs">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="text-left py-2 px-2 font-medium text-gray-600">
                                    Phone Number
                                  </th>
                                  <th className="text-left py-2 px-2 font-medium text-gray-600">
                                    Name
                                  </th>
                                  <th className="text-right py-2 px-2 font-medium text-gray-600">
                                    Monthly
                                  </th>
                                  <th className="text-left py-2 px-2 font-medium text-gray-600">
                                    Next Bill
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {phoneLines.map((line) => (
                                  <tr key={line.id} className="border-t border-gray-100">
                                    <td className="py-2 px-2 font-mono">
                                      {line.phone_number}
                                    </td>
                                    <td className="py-2 px-2 text-gray-600 truncate max-w-[120px]">
                                      {line.friendly_name || '-'}
                                    </td>
                                    <td className="py-2 px-2 text-right">
                                      {formatCurrency(line.customer_charge)}
                                    </td>
                                    <td className="py-2 px-2 text-gray-600">
                                      {line.next_bill_date
                                        ? new Date(line.next_bill_date).toLocaleDateString()
                                        : '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Payment Methods Card */}
            {selectedLocation.payment_methods && selectedLocation.payment_methods.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:col-span-2">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
                  <CreditCard className="w-5 h-5 mr-2 text-gray-600" />
                  Payment Methods
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedLocation.payment_methods.map((pm) => (
                    <div
                      key={pm.id}
                      className={`border rounded-lg p-4 ${
                        pm.is_active ? 'border-gray-200' : 'border-gray-100 bg-gray-50 opacity-60'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium capitalize">{pm.brand || pm.type}</span>
                        <div className="flex space-x-1">
                          {pm.is_primary_subscription && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                              Subscription
                            </span>
                          )}
                          {pm.is_primary_wallet && (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                              Wallet
                            </span>
                          )}
                        </div>
                      </div>
                      {pm.last4 && (
                        <p className="text-sm text-gray-600">
                          **** **** **** {pm.last4}
                          {pm.exp_month && pm.exp_year && (
                            <span className="ml-2">
                              Exp: {pm.exp_month}/{pm.exp_year}
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Transactions */}
            {showTransactions && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:col-span-2">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
                  <History className="w-5 h-5 mr-2 text-gray-600" />
                  Transaction History
                </h2>

                {loadingTransactions ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : transactions.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No transactions yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">
                            Date
                          </th>
                          <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">
                            Type
                          </th>
                          <th className="text-right py-2 px-3 text-sm font-medium text-gray-600">
                            Amount
                          </th>
                          <th className="text-right py-2 px-3 text-sm font-medium text-gray-600">
                            Balance
                          </th>
                          <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">
                            Description
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((tx) => (
                          <tr key={tx.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-2 px-3 text-sm">{formatDate(tx.created_at)}</td>
                            <td className="py-2 px-3">
                              <span
                                className={`text-xs px-2 py-1 rounded font-medium ${getTransactionTypeColor(
                                  tx.transaction_type
                                )}`}
                              >
                                {tx.transaction_type.replace('_', ' ')}
                              </span>
                            </td>
                            <td
                              className={`py-2 px-3 text-sm text-right font-medium ${
                                tx.amount >= 0 ? 'text-green-600' : 'text-red-600'
                              }`}
                            >
                              {tx.amount >= 0 ? '+' : ''}
                              {formatCurrency(tx.amount)}
                            </td>
                            <td className="py-2 px-3 text-sm text-right">
                              {formatCurrency(tx.balance_after)}
                            </td>
                            <td className="py-2 px-3 text-sm text-gray-600 truncate max-w-xs">
                              {tx.description || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Communications backfill modal */}
        {showBackfillModal && selectedLocation && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Backfill Communications — {selectedLocation.location_name || selectedLocation.location_id}
                </h3>
                <button onClick={() => setShowBackfillModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Re-pulls SMS/MMS/Voice from Twilio for the chosen UTC date range and bills them at current
                rates. Reads are free. Already-loaded messages are skipped, so this is safe to re-run. It
                triggers auto-recharge but never suspends — if the live billing flow suspends the account
                mid-run, the run pauses and waits for your decision.
              </p>

              {/* Range + options */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <label className="text-sm">
                  <span className="block text-gray-700 mb-1">Start date (UTC)</span>
                  <input type="date" value={backfillDateStart}
                    onChange={(e) => { setBackfillDateStart(e.target.value); setBackfillPreview(null); }}
                    className="w-full border border-gray-300 rounded px-2 py-1.5" />
                </label>
                <label className="text-sm">
                  <span className="block text-gray-700 mb-1">End date (UTC, inclusive)</span>
                  <input type="date" value={backfillDateEnd}
                    onChange={(e) => { setBackfillDateEnd(e.target.value); setBackfillPreview(null); }}
                    className="w-full border border-gray-300 rounded px-2 py-1.5" />
                </label>
              </div>
              <div className="flex items-center justify-between mb-4">
                <label className="flex items-center space-x-2 text-sm text-gray-700">
                  <input type="checkbox" checked={backfillIncludeVoice}
                    onChange={(e) => { setBackfillIncludeVoice(e.target.checked); setBackfillPreview(null); }} />
                  <span>Include voice calls</span>
                </label>
                <label className="text-sm text-gray-700">
                  <span className="mr-2">Auto-recharge:</span>
                  <select value={backfillFireMode}
                    onChange={(e) => setBackfillFireMode(e.target.value as 'fire_on_final' | 'fire_normal')}
                    className="border border-gray-300 rounded px-2 py-1">
                    <option value="fire_on_final">Fire on final (one top-up at end)</option>
                    <option value="fire_normal">Fire normal (top-up as it draws down)</option>
                  </select>
                </label>
              </div>

              <div className="flex items-center space-x-2 mb-4">
                <button onClick={fetchBackfillPreview} disabled={backfillPreviewLoading}
                  className="px-3 py-1.5 text-sm bg-gray-700 text-white rounded hover:bg-gray-800 disabled:opacity-50">
                  {backfillPreviewLoading ? 'Estimating…' : 'Preview cost'}
                </button>
                <button onClick={startBackfill} disabled={backfillStarting}
                  className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">
                  {backfillStarting ? 'Starting…' : 'Start backfill'}
                </button>
              </div>

              {backfillError && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
                  {backfillError}
                </div>
              )}

              {/* Preview result */}
              {backfillPreview && (
                <div className="mb-4 bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm">
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <div><span className="text-indigo-700">Current balance:</span> ${backfillPreview.currentBalance.toFixed(2)}</div>
                    <div><span className="text-indigo-700">Est. charge:</span>{' '}
                      {backfillPreview.isInternal ? 'Internal (no charge)' : `$${backfillPreview.estTotalCharge.toFixed(2)}`}</div>
                    <div><span className="text-indigo-700">Projected balance:</span> ${backfillPreview.projectedEndBalance.toFixed(2)}</div>
                  </div>
                  {backfillPreview.projectedEndBalance < 0 && !backfillPreview.isInternal && (
                    <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-2">
                      ⚠ This range would drive the balance negative (${backfillPreview.projectedEndBalance.toFixed(2)}).
                      Their card will be auto-charged to cover it. Give the customer a heads-up before starting,
                      or run fewer days at a time.
                    </p>
                  )}
                  {backfillPreview.capped && (
                    <p className="text-amber-800 mb-2">
                      Estimate hit the {backfillPreview.pageCap}-page scan cap — the real total is higher.
                      Narrow the range (or preview one day at a time) for an exact figure.
                    </p>
                  )}
                  <details>
                    <summary className="cursor-pointer text-xs text-indigo-700">Per-day breakdown</summary>
                    <table className="w-full text-xs mt-2">
                      <thead><tr className="text-indigo-700 text-left">
                        <th className="pr-2">Day</th><th className="pr-2">Msgs</th><th className="pr-2">SMS</th>
                        <th className="pr-2">MMS</th><th className="pr-2">Voice</th><th>Est $</th>
                      </tr></thead>
                      <tbody>
                        {backfillPreview.perDay.map((d) => (
                          <tr key={d.date} className={d.scanned ? '' : 'text-gray-400'}>
                            <td className="pr-2 font-mono">{d.date}</td>
                            <td className="pr-2">{d.messages.toLocaleString()}</td>
                            <td className="pr-2">{d.sms.toLocaleString()}</td>
                            <td className="pr-2">{d.mms.toLocaleString()}</td>
                            <td className="pr-2">{d.voice.toLocaleString()}</td>
                            <td>${d.estCharge.toFixed(2)}{d.scanned ? '' : ' (capped)'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </details>
                </div>
              )}

              {/* Recent runs */}
              {backfillRuns.length > 0 && (
                <div className="border-t border-gray-200 pt-3">
                  <div className="text-sm font-medium text-gray-700 mb-2">Recent runs</div>
                  <div className="space-y-2">
                    {backfillRuns.map((run) => (
                      <div key={run.id} className="border border-gray-200 rounded p-2 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="font-mono">{run.date_start}{run.date_end !== run.date_start ? ` → ${run.date_end}` : ''}</span>
                          <span className={
                            run.status === 'completed' ? 'text-green-700' :
                            run.status === 'running' ? 'text-blue-700' :
                            run.status === 'paused_suspended' ? 'text-amber-700' :
                            run.status === 'failed' ? 'text-red-700' : 'text-gray-600'
                          }>{run.status}</span>
                        </div>
                        <div className="text-gray-600 mt-1">
                          scanned {run.messages_scanned.toLocaleString()} · loaded {run.inserted_count.toLocaleString()} ·
                          dup {run.duplicate_count.toLocaleString()} · billed ${Number(run.total_debited).toFixed(2)}
                          {run.last_balance != null ? ` · bal $${Number(run.last_balance).toFixed(2)}` : ''}
                        </div>
                        {run.status === 'paused_suspended' && (
                          <div className="mt-1 text-amber-800">
                            Paused: account was suspended by the live billing flow. Fund the account, then continue.
                          </div>
                        )}
                        {run.error_text && <div className="mt-1 text-red-700">{run.error_text}</div>}
                        <div className="mt-2 flex items-center space-x-2">
                          {run.status === 'running' && (
                            <button onClick={() => backfillControl(run.id, 'abort')}
                              className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700">Abort</button>
                          )}
                          {run.status === 'paused_suspended' && (
                            <>
                              <button onClick={() => backfillControl(run.id, 'continue')}
                                className="px-2 py-1 bg-amber-600 text-white rounded hover:bg-amber-700">Continue anyway</button>
                              <button onClick={() => backfillControl(run.id, 'relaunch')}
                                className="px-2 py-1 bg-gray-600 text-white rounded hover:bg-gray-700">Retry (after funding)</button>
                            </>
                          )}
                          {run.status === 'failed' && (
                            <button onClick={() => backfillControl(run.id, 'relaunch')}
                              className="px-2 py-1 bg-gray-600 text-white rounded hover:bg-gray-700">Relaunch</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => selectedLocation && fetchBackfillRuns(selectedLocation.location_id)}
                    className="mt-2 text-xs text-indigo-600 hover:text-indigo-800">Refresh runs</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Batch download modal */}
        {showDownloadModal && selectedLocation && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Download Batches — {selectedLocation.location_name || selectedLocation.location_id}
                </h3>
                <button
                  onClick={() => setShowDownloadModal(false)}
                  disabled={dlRunning}
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                >
                  ✕
                </button>
              </div>

              {dlError && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
                  {dlError}
                </div>
              )}

              {dlLoading ? (
                <p className="text-sm text-gray-500 py-6 text-center">Loading batches…</p>
              ) : dlBatches.length === 0 && !dlError ? (
                <p className="text-sm text-gray-500 py-6 text-center">This location has no batches.</p>
              ) : dlBatches.length > 0 && (
                <>
                  {dlTotal > DOWNLOAD_PAGE_SIZE && (
                    <div className="flex items-center justify-between mb-2 text-sm text-gray-600">
                      <span>
                        Showing {(dlOffset + 1).toLocaleString()}–{(dlOffset + dlBatches.length).toLocaleString()} of{' '}
                        {dlTotal.toLocaleString()} batches
                      </span>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => fetchDownloadBatches(selectedLocation.location_id, dlOffset - DOWNLOAD_PAGE_SIZE)}
                          disabled={dlOffset === 0 || dlRunning}
                          className="px-2 py-1 border border-gray-300 rounded disabled:opacity-50"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => fetchDownloadBatches(selectedLocation.location_id, dlOffset + DOWNLOAD_PAGE_SIZE)}
                          disabled={dlOffset + DOWNLOAD_PAGE_SIZE >= dlTotal || dlRunning}
                          className="px-2 py-1 border border-gray-300 rounded disabled:opacity-50"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  <table className="w-full text-sm mb-4">
                    <thead>
                      <tr className="text-left text-gray-600 border-b border-gray-200">
                        <th className="py-2 pr-2 w-8">
                          <input
                            type="checkbox"
                            checked={dlBatches.every((b) => dlSelected.has(b.id))}
                            onChange={toggleAllOnDownloadPage}
                            disabled={dlRunning}
                          />
                        </th>
                        <th className="py-2 pr-2">Name</th>
                        <th className="py-2 pr-2">Created</th>
                        <th className="py-2 pr-2 text-right">Contacts</th>
                        <th className="py-2 pr-2 text-right">Clean</th>
                        <th className="py-2 pr-2">Status</th>
                        <th className="py-2">Download</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dlBatches.map((b) => {
                        const st = dlStatus[b.id];
                        return (
                          <tr key={b.id} className="border-b border-gray-100">
                            <td className="py-2 pr-2">
                              <input
                                type="checkbox"
                                checked={dlSelected.has(b.id)}
                                onChange={() => toggleDownloadBatch(b)}
                                disabled={dlRunning}
                              />
                            </td>
                            <td className="py-2 pr-2 break-all">{b.batch_name || <span className="text-gray-400">(unnamed)</span>}</td>
                            <td className="py-2 pr-2 whitespace-nowrap">{new Date(b.created_at).toLocaleDateString()}</td>
                            <td className="py-2 pr-2 text-right">{(b.total_contacts ?? 0).toLocaleString()}</td>
                            <td className="py-2 pr-2 text-right">{(b.clean_count ?? 0).toLocaleString()}</td>
                            <td className="py-2 pr-2">{b.status}</td>
                            <td className={`py-2 text-xs ${
                              st?.startsWith('Error') ? 'text-red-700' : st === 'Done' ? 'text-green-700' : 'text-gray-600'
                            }`}>
                              {st || ''}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </>
              )}

              <div className="flex items-center justify-between border-t border-gray-200 pt-4">
                <label className="text-sm text-gray-700">
                  <span className="mr-2">Contacts:</span>
                  <select
                    value={dlFilter}
                    onChange={(e) => setDlFilter(e.target.value as 'all' | 'clean' | 'bad')}
                    disabled={dlRunning}
                    className="border border-gray-300 rounded px-2 py-1"
                  >
                    <option value="all">All</option>
                    <option value="clean">Clean only</option>
                    <option value="bad">Bad only</option>
                  </select>
                </label>
                <button
                  onClick={runBatchDownloads}
                  disabled={dlRunning || dlSelected.size === 0}
                  className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                >
                  {dlRunning ? 'Downloading…' : `Download ${dlSelected.size} selected`}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Files download one at a time. Your browser may ask once to allow multiple downloads.
              </p>
            </div>
          </div>
        )}

        {/* Stage 1d: Replay confirmation modal */}
        {showReplayConfirm && recoverySummary && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
              <div className="flex items-center space-x-2 mb-4">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <h2 className="text-lg font-semibold text-gray-900">Confirm event replay</h2>
              </div>
              <div className="space-y-3 text-sm text-gray-700">
                <p>
                  This will replay{' '}
                  <strong>{recoverySummary.unresolvedCount.toLocaleString()}</strong>{' '}
                  rejected Twilio webhook events through the normal billing flow for{' '}
                  <strong>{recoverySummary.billingDisplayName || selectedLocation.location_id}</strong>.
                </p>
                <div className="bg-gray-50 border border-gray-200 rounded p-3 space-y-1">
                  <div>
                    <span className="text-gray-500">Sub-account SID:</span>{' '}
                    <span className="font-mono">{recoverySummary.accountSid}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Estimated wallet impact:</span>{' '}
                    {recoverySummary.isInternal
                      ? 'Internal user (no charge)'
                      : `$${recoverySummary.estimatedWalletImpact.toFixed(2)} ${
                          recoverySummary.breakdownIsEstimate ? '(approximate)' : ''
                        }`}
                  </div>
                </div>
                <ul className="list-disc ml-5 text-xs text-gray-600 space-y-1">
                  <li>Events run in batches of {recoverySummary.replayBatchSize} rows.</li>
                  <li>
                    Idempotent — already-recorded message SIDs are skipped automatically (counted as
                    duplicates).
                  </li>
                  <li>
                    Stats backfill to the original event time (twilio_created_at) so dashboards
                    reflect the correct dates.
                  </li>
                </ul>
              </div>
              <div className="flex justify-end space-x-2 mt-6">
                <button
                  onClick={() => setShowReplayConfirm(false)}
                  disabled={replayInProgress}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowReplayConfirm(false);
                    runReplay(selectedLocation.location_id, recoverySummary.accountSid);
                  }}
                  disabled={replayInProgress}
                  className="px-4 py-2 text-sm text-white bg-amber-600 hover:bg-amber-700 rounded disabled:opacity-50"
                >
                  Replay {recoverySummary.unresolvedCount.toLocaleString()} events
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Location Billing</h1>
          <p className="text-gray-500 mt-1">Manage billing configuration for all locations</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleBulkSyncSubscriptionPeriods}
            disabled={bulkSyncing}
            title="Sync billing_cycle_start/end from Stripe for every location with a subscription. Use to recover from drift caused by webhook outages or pre-fix data."
            className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-200 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${bulkSyncing ? 'animate-spin' : ''}`} />
            <span>{bulkSyncing ? 'Syncing All...' : 'Sync All from Stripe'}</span>
          </button>
          <button
            onClick={fetchLocations}
            disabled={loading}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {bulkSyncResult && (
        <div className={`rounded-xl p-4 ${bulkSyncResult.success ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {bulkSyncResult.message}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{error}</div>
      )}

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search by location ID or name..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Results */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading && locations.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : locations.length === 0 ? (
          <div className="text-center py-12">
            <Wallet className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Locations Found</h3>
            <p className="text-gray-500">
              {search ? 'Try a different search term' : 'No billing configurations yet'}
            </p>
          </div>
        ) : (
          <>
            {renderPaginationBar('top')}
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {renderSortHeader('name', 'Location', 'left')}
                  {renderSortHeader('wallet', 'Wallet Balance', 'right')}
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Plan</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                    Billing Limits
                  </th>
                  {renderSortHeader('created', 'Created', 'left')}
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {locations.map((loc) => (
                  <tr
                    key={loc.location_id}
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                    onClick={() => fetchLocationDetail(loc.location_id)}
                  >
                    <td className="py-3 px-4">
                      <p className="font-medium text-gray-900 flex items-center gap-2">
                        {loc.location_name || loc.location_id}
                        {loc.is_internal && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            Internal
                          </span>
                        )}
                        {warnings30023.has(loc.location_id) && (() => {
                          const w = warnings30023.get(loc.location_id)!;
                          return (
                            <span
                              title={`Hitting daily message caps — ${w.events_today} capped today (${w.pct_today}% of ${w.outbound_today} sent). May lower trust score.`}
                              className="inline-flex items-center text-amber-600"
                              aria-label="Hitting daily message caps"
                            >
                              <AlertTriangle className="w-4 h-4" />
                            </span>
                          );
                        })()}
                      </p>
                      <p className="text-xs text-gray-500">
                        <span className="font-mono">{loc.location_id}</span>
                        {!loc.has_billing_config && (
                          <span className="ml-2 text-yellow-600">(Not configured)</span>
                        )}
                      </p>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span
                        className={`font-semibold ${
                          loc.wallet_balance < 5
                            ? 'text-red-600'
                            : loc.wallet_balance < 20
                            ? 'text-yellow-600'
                            : 'text-green-600'
                        }`}
                      >
                        {formatCurrency(loc.wallet_balance)}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm">{loc.plan_name || '-'}</span>
                    </td>
                    <td className="py-3 px-4">
                      {getSubscriptionStatusBadge(loc.subscription_status)}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col gap-0.5 text-xs">
                        <span className="text-gray-700">
                          <span className="text-gray-500">Recharge:</span>{' '}
                          {loc.auto_recharge_enabled ? (
                            <span className="font-medium">
                              {formatCurrency(loc.auto_recharge_amount)} @{' '}
                              {formatCurrency(loc.auto_recharge_threshold)}
                            </span>
                          ) : (
                            <span className="text-gray-500">off</span>
                          )}
                        </span>
                        <span className="text-gray-700">
                          <span className="text-gray-500">Credit:</span>{' '}
                          <span className="font-medium">
                            {formatCurrency(Math.abs(loc.negative_balance_threshold ?? 0))}
                          </span>
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">
                      {formatDateShort(loc.created_at)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          fetchLocationDetail(loc.location_id);
                        }}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {renderPaginationBar('bottom')}
          </>
        )}
      </div>
    </div>
  );
}
